import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import {
    Eye, RefreshCw, X, Search, Plus, Trash2, Save,
    FileText, Download, Receipt, Users, CalendarDays,
    Building2, ChevronDown, Printer
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const gradBlue = {
    background: "linear-gradient(135deg, #0c1445 0%, #1a2f7a 50%, #1e40af 100%)",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const esc = (s) =>
    String(s ?? "").replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
    );

const fmt = (n) =>
    Number(n)
        ? Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const fmtDateShort = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "";

// Field accessors for Receipt
const fR = {
    id: (r) => r.id || r.Id || 0,
    receiptNo: (r) => r.receiptNo || r.ReceiptNo || "",
    paymentDate: (r) => r.paymentDate || r.PaymentDate || "",
    accountNo: (r) => r.accountNo || r.AccountNo || "",
    paymentMode: (r) => r.paymentMode || r.PaymentMode || "CASH",
    receivedFrom: (r) => r.receivedFrom || r.ReceivedFrom || "",
    tin: (r) => r.tin || r.TIN || "",
    address: (r) => r.address || r.Address || "",
    soId: (r) => r.soId || r.SoId || null,
    soNo: (r) => r.soNo || r.SoNo || "",
    soPrefix: (r) => r.soPrefix || r.SoPrefix || "",
    totalAmount: (r) => r.totalAmount || r.TotalAmount || 0,
    details: (r) => r.details || r.Details || [],
    checkNo: (r) => r.checkNo || r.CheckNo || "",
    bank: (r) => r.bank || r.Bank || "",
    checkDate: (r) => r.checkDate || r.CheckDate || "",
    createdBy: (r) => r.createdBy || r.CreatedBy || "",
};

// Field accessors for SO
const fSO = {
    id: (r) => r.id || r.Id || 0,
    soNo: (r) => r.soNo || r.SoNo || "",
    soPrefix: (r) => r.soPrefix || r.SoPrefix || "VS",
    orderedBy: (r) => r.orderedBy || r.OrderedBy || "",
    address: (r) => r.address || r.Address || "",
    totalPayable: (r) => {
        const items = r.items || r.Items || [];
        return items.reduce((sum, i) => {
            const amt = parseFloat(i.amount || i.Amount) ||
                (parseFloat(i.qty || i.Qty) * parseFloat(i.unitPrice || i.UnitPrice)) || 0;
            return sum + amt;
        }, 0);
    },
};

const EMPTY_FORM = () => ({
    receiptNo: "",
    paymentDate: new Date().toISOString().split("T")[0],
    accountNo: "",
    paymentMode: "CASH",
    receivedFrom: "",
    tin: "",
    address: "",
    soId: null,
    soNo: "",
    soPrefix: "",
    checkNo: "",
    bank: "",
    checkDate: "",
    details: [{ description: "", amount: 0 }],
    createdBy: "",
});

// ── PDF Export ────────────────────────────────────────────────────────────────
const exportReceiptPDF = async (data) => {
    if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
        });
    }

    const details = fR.details(data);
    const MIN_ROWS = 5;
    const displayDetails = [
        ...details,
        ...Array.from({ length: Math.max(0, MIN_ROWS - details.length) }, () => ({})),
    ];

    const rowsHTML = displayDetails.map((d) => `
        <tr>
            <td style="padding:5px 8px;border-bottom:0.5px solid #e5e7eb;font-size:9px">${esc(d.description || "")}</td>
            <td style="padding:5px 8px;border-bottom:0.5px solid #e5e7eb;font-size:9px;text-align:right;font-weight:700">${d.amount ? fmt(d.amount) : ""}</td>
        </tr>`).join("");

    const isCheck = fR.paymentMode(data) === "CHECK";

    const html = `
    <div style="font-family:'Times New Roman',Times,serif;width:160mm;padding:5mm 7mm;background:#fff;border:1px solid #ccc">
        <div style="text-align:center;padding-bottom:5px;border-bottom:2px solid #000;margin-bottom:6px">
            <div style="font-size:12px;font-weight:900;letter-spacing:0.08em;text-transform:uppercase">MEGA SOFT HYGIENIC PRODUCTS, INC.</div>
            <div style="font-size:7px;color:#555;margin-top:2px">Tanayao St. No. tavella, Quezon City · Tel: 4503-4901 loc. 300 · VAT Reg. TIN: 230-227-179-00000</div>
        </div>

        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
            <div style="font-size:18px;font-weight:900;letter-spacing:0.05em">COLLECTION RECEIPT</div>
            <div>
                <span style="font-size:9px;color:#555;font-weight:700">No. </span>
                <span style="font-size:20px;font-weight:900;letter-spacing:2px;font-family:monospace">${esc(fR.receiptNo(data))}</span>
            </div>
        </div>

        <div style="display:flex;gap:8px;margin-bottom:6px">
            <div style="border:1px solid #000;padding:3px 8px;font-size:9px;display:flex;align-items:center;gap:4px">
                <span style="font-weight:900">${isCheck ? "☐" : "☑"}</span> CASH
            </div>
            <div style="border:1px solid #000;padding:3px 8px;font-size:9px;display:flex;align-items:center;gap:4px">
                <span style="font-weight:900">${isCheck ? "☑" : "☐"}</span> CHECK
            </div>
            <div style="border:1px solid #ccc;padding:3px 10px;font-size:9px;color:#166534;font-weight:900;margin-left:auto">PAID</div>
            <div style="font-size:8px;margin-left:10px">
                <div><strong>Payment Date:</strong> ${fmtDateShort(fR.paymentDate(data))}</div>
                <div><strong>Account No.:</strong> ${esc(fR.accountNo(data))}</div>
            </div>
        </div>

        <div style="border:1px solid #000;padding:4px 8px;font-size:8px;font-weight:900;margin-bottom:4px;background:#f0f0f0">RECEIVED FROM</div>
        <table style="width:100%;border-collapse:collapse;font-size:9px;margin-bottom:8px">
            <tr>
                <td style="padding:3px 0;width:110px;font-weight:700;color:#555">Registered Name :</td>
                <td style="border-bottom:1px solid #000;font-weight:700">${esc(fR.receivedFrom(data))}</td>
            </tr>
            <tr>
                <td style="padding:3px 0;font-weight:700;color:#555">TIN :</td>
                <td style="border-bottom:1px dotted #ccc">${esc(fR.tin(data))}</td>
            </tr>
            <tr>
                <td style="padding:3px 0;font-weight:700;color:#555">Business Address :</td>
                <td style="border-bottom:1px dotted #ccc">${esc(fR.address(data))}</td>
            </tr>
        </table>

        <table style="width:100%;border-collapse:collapse;border:1px solid #999;margin-bottom:8px">
            <thead>
                <tr style="background:#1e3a8a">
                    <th style="padding:5px 8px;color:#fff;font-size:9px;text-align:left;border-right:0.5px solid rgba(255,255,255,0.3)">Description of Transaction/Nature of Service</th>
                    <th style="padding:5px 8px;color:#fff;font-size:9px;text-align:right;width:90px">Amount</th>
                </tr>
            </thead>
            <tbody>${rowsHTML}</tbody>
        </table>

        <div style="margin-bottom:8px">
            <div style="font-size:8.5px;font-weight:900;border-bottom:1px solid #ccc;padding-bottom:3px;margin-bottom:6px">Payment Details</div>
            <table style="font-size:9px;border-collapse:collapse">
                <tr><td style="padding:2px 12px 2px 0;color:#555;font-weight:700">Check No. :</td><td style="border-bottom:1px dotted #ccc;min-width:100px">${esc(fR.checkNo(data))}</td></tr>
                <tr><td style="padding:2px 12px 2px 0;color:#555;font-weight:700">Bank :</td><td style="border-bottom:1px dotted #ccc">${esc(fR.bank(data))}</td></tr>
                <tr><td style="padding:2px 12px 2px 0;color:#555;font-weight:700">Check Date :</td><td style="border-bottom:1px dotted #ccc">${esc(fR.checkDate(data) ? fmtDateShort(fR.checkDate(data)) : "")}</td></tr>
            </table>
        </div>

        <div style="display:flex;justify-content:flex-end;align-items:center;gap:16px;border-top:1px solid #ccc;padding-top:6px">
            <div style="font-size:7.5px;max-width:120px;border:1px solid #000;padding:5px;text-align:center;font-weight:900;font-style:italic;line-height:1.4">
                "THIS DOCUMENT IS NOT VALID FOR CLAIM OF INPUT TAX."
            </div>
            <div>
                <div style="font-size:8px;color:#555;font-weight:700;text-align:right">TOTAL PAID AMOUNT</div>
                <div style="font-size:20px;font-weight:900;color:#166534;text-align:right;font-family:monospace">${fmt(fR.totalAmount(data))}</div>
                <div style="font-size:7.5px;color:#555;text-align:right;margin-top:2px">Invoice Reference No.: ${esc(fR.soPrefix(data))}${esc(fR.soNo(data))}</div>
            </div>
        </div>
    </div>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    document.body.appendChild(el);
    try {
        await window.html2pdf(el, {
            margin: [4, 4, 4, 4],
            filename: `CollectionReceipt_${fR.receiptNo(data)}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        });
    } finally {
        document.body.removeChild(el);
    }
};

// ── Receipt Document Preview ──────────────────────────────────────────────────
const ReceiptDocument = ({ data }) => {
    const details = fR.details(data);
    const MIN_ROWS = 5;
    const displayDetails = [
        ...details,
        ...Array.from({ length: Math.max(0, MIN_ROWS - details.length) }, () => ({})),
    ];
    const isCheck = fR.paymentMode(data) === "CHECK";

    return (
        <div style={{ fontFamily: "'Times New Roman', Times, serif", background: "#fff", border: "1px solid #ccc" }}>
            {/* Header */}
            <div style={{ textAlign: "center", padding: "12px 24px 8px", borderBottom: "2.5px solid #000" }}>
                <div style={{ fontSize: "14px", fontWeight: "900", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    MEGA SOFT HYGIENIC PRODUCTS, INC.
                </div>
                <div style={{ fontSize: "8px", color: "#555", marginTop: "2px" }}>
                    Tanayao St. No. tavella, Quezon City · Tel: 4503-4901 loc. 300 · VAT Reg. TIN: 230-227-179-00000
                </div>
            </div>

            {/* Title + Receipt No */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 24px 8px" }}>
                <div style={{ fontSize: "20px", fontWeight: "900", letterSpacing: "0.05em" }}>COLLECTION RECEIPT</div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <span style={{ fontSize: "10px", color: "#555", fontWeight: "700" }}>No. </span>
                    <span style={{ fontSize: "22px", fontWeight: "900", letterSpacing: "2px", fontFamily: "monospace" }}>
                        {fR.receiptNo(data)}
                    </span>
                </div>
            </div>

            {/* Payment mode + Date */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "0 24px 10px", flexWrap: "wrap" }}>
                {["CASH", "CHECK"].map(mode => (
                    <div key={mode} style={{ border: "1px solid #000", padding: "3px 10px", fontSize: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontWeight: "900" }}>{(isCheck ? mode === "CHECK" : mode === "CASH") ? "☑" : "☐"}</span>
                        {mode}
                    </div>
                ))}
                <div style={{ border: "1px solid #166534", padding: "3px 12px", fontSize: "10px", color: "#166534", fontWeight: "900", marginLeft: "8px" }}>
                    PAID
                </div>
                <div style={{ marginLeft: "auto", fontSize: "10px", fontFamily: "Arial, sans-serif" }}>
                    <div><strong>Payment Date:</strong> {fmtDateShort(fR.paymentDate(data))}</div>
                    <div><strong>Account No.:</strong> {fR.accountNo(data) || "\u00A0"}</div>
                </div>
            </div>

            {/* Received From */}
            <div style={{ background: "#f0f0f0", padding: "4px 24px", borderTop: "1px solid #000", borderBottom: "1px solid #000", fontSize: "10px", fontWeight: "900" }}>
                RECEIVED FROM
            </div>
            <div style={{ padding: "8px 24px 10px", fontFamily: "Arial, sans-serif" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "11px", width: "100%" }}>
                    <tbody>
                        {[
                            ["Registered Name :", fR.receivedFrom(data)],
                            ["TIN :", fR.tin(data)],
                            ["Business Address :", fR.address(data)],
                        ].map(([label, val]) => (
                            <tr key={label}>
                                <td style={{ padding: "3px 10px 3px 0", color: "#555", fontWeight: "700", whiteSpace: "nowrap", width: "140px" }}>{label}</td>
                                <td style={{ borderBottom: "1px solid #999", fontWeight: "700", minWidth: "200px", paddingBottom: "2px" }}>{val || "\u00A0"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Details Table */}
            <div style={{ padding: "0 24px 12px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #999" }}>
                    <thead>
                        <tr style={{ background: "#1e3a8a" }}>
                            <th style={{ padding: "6px 10px", color: "#fff", fontSize: "10px", textAlign: "left", borderRight: "0.5px solid rgba(255,255,255,0.3)" }}>
                                Description of Transaction/Nature of Service
                            </th>
                            <th style={{ padding: "6px 10px", color: "#fff", fontSize: "10px", textAlign: "right", width: "110px" }}>
                                Amount
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayDetails.map((d, i) => (
                            <tr key={i} style={{ borderBottom: "0.5px solid #e5e7eb", background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                                <td style={{ padding: "6px 10px", fontSize: "11px", borderRight: "0.5px solid #e5e7eb" }}>{d.description || "\u00A0"}</td>
                                <td style={{ padding: "6px 10px", fontSize: "11px", textAlign: "right", fontWeight: "700" }}>{d.amount ? fmt(d.amount) : "\u00A0"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Payment Details */}
            <div style={{ padding: "0 24px 12px", fontFamily: "Arial, sans-serif" }}>
                <div style={{ fontSize: "10px", fontWeight: "900", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "8px" }}>Payment Details</div>
                <table style={{ fontSize: "10px", borderCollapse: "collapse" }}>
                    <tbody>
                        {[
                            ["Check No. :", fR.checkNo(data)],
                            ["Bank :", fR.bank(data)],
                            ["Check Date :", fR.checkDate(data) ? fmtDateShort(fR.checkDate(data)) : ""],
                        ].map(([label, val]) => (
                            <tr key={label}>
                                <td style={{ padding: "2px 14px 2px 0", color: "#555", fontWeight: "700" }}>{label}</td>
                                <td style={{ borderBottom: "1px dotted #ccc", minWidth: "140px", paddingBottom: "2px" }}>{val || "\u00A0"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Footer — Total + notice */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 24px 14px", borderTop: "1px solid #ccc" }}>
                <div style={{ border: "1.5px solid #000", padding: "8px 12px", textAlign: "center", maxWidth: "150px", fontSize: "8.5px", fontWeight: "900", fontStyle: "italic", lineHeight: "1.5" }}>
                    "THIS DOCUMENT IS<br />NOT VALID FOR CLAIM<br />OF INPUT TAX."
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "9px", color: "#555", fontWeight: "700", fontFamily: "Arial, sans-serif" }}>TOTAL PAID AMOUNT</div>
                    <div style={{ fontSize: "28px", fontWeight: "900", color: "#166534", letterSpacing: "1px", fontFamily: "monospace" }}>
                        {fmt(fR.totalAmount(data))}
                    </div>
                    <div style={{ fontSize: "8px", color: "#555", fontFamily: "Arial, sans-serif", marginTop: "2px" }}>
                        Invoice Reference No.: {fR.soPrefix(data)}{fR.soNo(data)}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Detail Row Editor ─────────────────────────────────────────────────────────
const DetailRow = ({ index, item, onChange, onRemove }) => (
    <tr className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}>
        <td className="px-2 py-1.5 text-center text-slate-400 text-[10px] font-mono w-8">{index + 1}</td>
        <td className="px-1.5 py-1.5">
            <input type="text" value={item.description ?? ""} placeholder="Description of transaction..."
                onChange={e => onChange("description", e.target.value)}
                className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-semibold" />
        </td>
        <td className="px-1.5 py-1.5 w-32">
            <input type="number" value={item.amount ?? ""} placeholder="0.00" min="0" step="0.01"
                onChange={e => onChange("amount", parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-bold text-blue-700" />
        </td>
        <td className="px-1.5 py-1.5 w-10 text-center">
            <button onClick={onRemove} className="text-red-400 hover:text-red-600 transition-colors cursor-pointer">
                <X size={13} />
            </button>
        </td>
    </tr>
);

// ═════════════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════════
const CollectionReceipt = () => {
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch { } }
    }, []);
    const userName = currentUser?.fullName || currentUser?.name || currentUser?.username || "Unknown User";

    // ── State ─────────────────────────────────────────────────────────────────
    const [records, setRecords] = useState([]);
    const [salesOrders, setSalesOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [soLoading, setSoLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [soSearch, setSoSearch] = useState("");
    const [showSODropdown, setShowSODropdown] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM());

    // ── Fetch Receipts ────────────────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapReceipts`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setRecords(await res.json());
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to load Receipts", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setLoading(false); }
    }, []);

    // ── Fetch Sales Orders ────────────────────────────────────────────────────
    const fetchSalesOrders = useCallback(async () => {
        setSoLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/SalesOrderScrap`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setSalesOrders(await res.json());
        } catch (err) {
            // silent — SO linking is optional
        } finally { setSoLoading(false); }
    }, []);

    useEffect(() => { fetchRecords(); fetchSalesOrders(); }, [fetchRecords, fetchSalesOrders]);

    // ── Auto-generate Receipt No ──────────────────────────────────────────────
    const generateReceiptNo = useCallback(() => {
        const max = records
            .map(r => parseInt(fR.receiptNo(r), 10))
            .filter(n => !isNaN(n))
            .reduce((m, n) => Math.max(m, n), 0);
        return String(max + 1).padStart(6, "0");
    }, [records]);

    // ── Open new modal ────────────────────────────────────────────────────────
    const openNew = () => {
        setForm({ ...EMPTY_FORM(), receiptNo: generateReceiptNo(), createdBy: userName });
        setSoSearch("");
        setShowSODropdown(false);
        setShowForm(true);
    };

    // ── Select SO ─────────────────────────────────────────────────────────────
    const selectSO = (so) => {
        const total = fSO.totalPayable(so);
        setForm(prev => ({
            ...prev,
            soId: fSO.id(so),
            soNo: fSO.soNo(so),
            soPrefix: fSO.soPrefix(so),
            receivedFrom: prev.receivedFrom || fSO.orderedBy(so),
            address: prev.address || fSO.address(so),
            details: [{
                description: `Payment for ${fSO.soPrefix(so)}${fSO.soNo(so)} (${fSO.orderedBy(so)})`,
                amount: total,
            }],
        }));
        setShowSODropdown(false);
        setSoSearch("");
    };

    // ── Clear SO link ─────────────────────────────────────────────────────────
    const clearSO = () => {
        setForm(prev => ({ ...prev, soId: null, soNo: "", soPrefix: "" }));
    };

    // ── Detail handlers ───────────────────────────────────────────────────────
    const updateDetail = (idx, field, value) => {
        setForm(prev => {
            const details = [...prev.details];
            details[idx] = { ...details[idx], [field]: value };
            return { ...prev, details };
        });
    };
    const removeDetail = (idx) => {
        setForm(prev => ({ ...prev, details: prev.details.filter((_, i) => i !== idx) }));
    };
    const addDetail = () => {
        setForm(prev => ({ ...prev, details: [...prev.details, { description: "", amount: 0 }] }));
    };

    const totalAmount = form.details.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

    // ── Submit — always POST, no nulls ────────────────────────────────────────
    const handleSubmit = async () => {
        if (!form.receivedFrom.trim())
            return Swal.fire({ icon: "warning", title: "Registered Name is required", confirmButtonColor: "#1d4ed8" });

        const validDetails = form.details
            .filter(d => d.description || d.amount)
            .map(d => ({
                description: d.description?.trim() || "",
                amount: parseFloat(d.amount) || 0,
            }));

        if (validDetails.length === 0)
            return Swal.fire({ icon: "warning", title: "Add at least one detail line", confirmButtonColor: "#1d4ed8" });

        setFormLoading(true);
        try {
            // ✅ No nulls — every field has a fallback
            const payload = {
                receiptNo: form.receiptNo?.trim() || "",
                paymentDate: form.paymentDate || new Date().toISOString().split("T")[0],
                accountNo: form.accountNo?.trim() || "",
                paymentMode: form.paymentMode?.trim() || "CASH",
                receivedFrom: form.receivedFrom?.trim() || "",
                tin: form.tin?.trim() || "",
                address: form.address?.trim() || "",
                soId: form.soId || 0,
                soNo: form.soNo?.trim() || "",
                soPrefix: form.soPrefix?.trim() || "",
                checkNo: form.checkNo?.trim() || "",
                bank: form.bank?.trim() || "",
                checkDate: form.checkDate || null,
                totalAmount: totalAmount,
                createdBy: userName || "",
                details: validDetails,
            };

            const res = await fetch(`${API_BASE_URL}api/ScrapReceipts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e?.message || `Server error: ${res.status}`);
            }

            setShowForm(false);
            fetchRecords();

            // Log — silent fail
            fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "CREATE", module: "Collection Receipt",
                    description: `Created Receipt #${form.receiptNo}`,
                    userId: currentUser?.id,
                    username: currentUser?.username,
                    fullName: userName,
                }),
            }).catch(() => { });

            await Swal.fire({ icon: "success", title: "Collection Receipt Created!", timer: 2000, showConfirmButton: false });

        } catch (err) {
            Swal.fire({ icon: "error", title: "Save Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally {
            setFormLoading(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (row) => {
        const id = fR.id(row);
        const { isConfirmed } = await Swal.fire({
            icon: "warning", title: "Delete Receipt?",
            html: `Delete Receipt <strong>#${fR.receiptNo(row)}</strong>?<br/>This cannot be undone.`,
            showCancelButton: true, confirmButtonText: "Yes, Delete", cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
        });
        if (!isConfirmed) return;
        try {
            const res = await fetch(`${API_BASE_URL}api/ScrapReceipts/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            await Swal.fire({ icon: "success", title: "Deleted!", timer: 1800, showConfirmButton: false });
            setCurrentPage(1);
            fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Delete Failed", text: err.message, confirmButtonColor: "#dc2626" });
        }
    };

    // ── Filter + paginate ─────────────────────────────────────────────────────
    const filtered = useMemo(() =>
        records.filter(row => {
            const q = search.toLowerCase();
            return !q
                || fR.receiptNo(row).toLowerCase().includes(q)
                || fR.receivedFrom(row).toLowerCase().includes(q)
                || fR.soNo(row).toLowerCase().includes(q);
        }),
        [records, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const paginBtn = (d) => `px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${d ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"}`;

    // ── Filtered SOs for dropdown ─────────────────────────────────────────────
    const filteredSOs = salesOrders.filter(so => {
        const q = soSearch.toLowerCase();
        return !q
            || fSO.soNo(so).toLowerCase().includes(q)
            || fSO.orderedBy(so).toLowerCase().includes(q);
    }).slice(0, 8);

    // ═══════════════════════════════════════════════════════════════════════════
    return (
        <div className="min-h-screen bg-slate-100 p-8 font-sans">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">

                {/* PAGE HEADER */}
                <div style={gradBlue} className="px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl">
                            <Receipt className="text-white" size={22} />
                        </div>
                        <div>
                            <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Sales Management</p>
                            <h1 className="text-white font-extrabold text-xl leading-tight">Collection Receipts</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-1.5">
                            <Users size={14} className="text-white/70" />
                            <span className="text-white text-sm font-semibold">{userName}</span>
                        </div>
                        <button onClick={openNew}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                            style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", boxShadow: "0 2px 10px rgba(37,99,235,0.35)" }}>
                            <Plus size={16} /> New Receipt
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {/* TOOLBAR */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search receipt no, customer, SO no..."
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                        <button onClick={fetchRecords} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer">
                            <RefreshCw size={15} />
                        </button>
                    </div>

                    {!loading && <p className="text-xs text-slate-500 mb-3">{filtered.length} record{filtered.length !== 1 ? "s" : ""} found</p>}

                    {/* TABLE */}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                            <RefreshCw size={20} className="animate-spin" />
                            <span className="text-sm font-medium">Loading Receipts...</span>
                        </div>
                    ) : paginated.length === 0 ? (
                        <div className="text-center py-16 text-slate-400 text-sm">No Collection Receipts found.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        {["#", "Receipt No.", "Date", "Received From", "SO Ref.", "Payment Mode", "Total Amount", "Actions"].map((h, i, arr) => (
                                            <th key={i} className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap ${h === "Actions" || h === "Total Amount" ? "text-center" : ""} ${i === 0 ? "rounded-tl-xl" : ""} ${i === arr.length - 1 ? "rounded-tr-xl" : ""}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((row, idx) => (
                                        <tr key={fR.id(row) || idx} className={`border-b border-gray-100 transition-colors hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                            <td className="px-3 py-3 text-center text-slate-400 text-xs w-10">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                            <td className="px-3 py-3 font-bold text-blue-700 whitespace-nowrap font-mono">
                                                #{fR.receiptNo(row) || "—"}
                                            </td>
                                            <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{fmtDate(fR.paymentDate(row))}</td>
                                            <td className="px-3 py-3 text-slate-700 font-semibold">{fR.receivedFrom(row) || "—"}</td>
                                            <td className="px-3 py-3">
                                                {fR.soNo(row) ? (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 font-mono">
                                                        {fR.soPrefix(row)}{fR.soNo(row)}
                                                    </span>
                                                ) : <span className="text-slate-400 text-xs">—</span>}
                                            </td>
                                            <td className="px-3 py-3">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${fR.paymentMode(row) === "CHECK" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                                                    {fR.paymentMode(row)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-center font-bold text-blue-700">{fmt(fR.totalAmount(row))}</td>
                                            <td className="px-3 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={() => setSelectedRow(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">
                                                        <Eye size={13} /> View
                                                    </button>
                                                    <button onClick={() => handleDelete(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all cursor-pointer">
                                                        <Trash2 size={13} /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION */}
                    {!loading && filtered.length > PAGE_SIZE && (
                        <div className="flex justify-between items-center mt-5">
                            <span className="text-xs text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                            <div className="flex gap-1.5 items-center">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>«</button>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) page = i + 1;
                                    else if (currentPage <= 3) page = i + 1;
                                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                    else page = currentPage - 2 + i;
                                    return (
                                        <button key={page} onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all cursor-pointer ${page === currentPage ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* PAGE FOOTER */}
                <div className="px-8 py-3 border-t border-gray-100 bg-slate-50 flex justify-between items-center">
                    <p className="text-[10px] text-slate-400 italic">This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</p>
                    <span className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════
                NEW RECEIPT MODAL
                ══════════════════════════════════════════════════════ */}
            {showForm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden"
                        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        {/* Modal Header */}
                        <div style={gradBlue} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><Receipt size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em]">Mega Soft · Collection</p>
                                    <h2 className="text-white font-extrabold text-base">New Collection Receipt</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-right">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Receipt No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-widest font-mono">#{form.receiptNo}</p>
                                </div>
                                <button onClick={() => setShowForm(false)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer transition-all" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-7 bg-slate-50 space-y-5">

                            {/* SO Link */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Link to Sales Order (Optional)</h3>
                                {form.soId ? (
                                    <div className="flex items-center gap-3 p-3 bg-blue-50 border-2 border-blue-200 rounded-xl">
                                        <div className="flex-1">
                                            <span className="text-xs text-slate-500">Linked SO:</span>
                                            <span className="ml-2 font-bold text-blue-700 font-mono text-sm">{form.soPrefix}{form.soNo}</span>
                                        </div>
                                        <button onClick={clearSO} className="text-red-400 hover:text-red-600 cursor-pointer transition-colors">
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="relative">
                                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                            <input
                                                type="text"
                                                placeholder="Search SO number or customer..."
                                                value={soSearch}
                                                onFocus={() => setShowSODropdown(true)}
                                                onChange={e => { setSoSearch(e.target.value); setShowSODropdown(true); }}
                                                className="w-full pl-9 pr-10 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                            />
                                            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        </div>
                                        {showSODropdown && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto">
                                                {soLoading ? (
                                                    <div className="p-3 text-xs text-slate-400 text-center">Loading SOs...</div>
                                                ) : filteredSOs.length === 0 ? (
                                                    <div className="p-3 text-xs text-slate-400 text-center">No Sales Orders found</div>
                                                ) : filteredSOs.map(so => (
                                                    <div key={fSO.id(so)}
                                                        onClick={() => selectSO(so)}
                                                        className="flex items-center justify-between px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                                        <div>
                                                            <span className="font-bold text-blue-700 font-mono text-xs">{fSO.soPrefix(so)}{fSO.soNo(so)}</span>
                                                            <span className="ml-2 text-slate-600 text-xs">{fSO.orderedBy(so)}</span>
                                                        </div>
                                                        <span className="text-xs font-bold text-blue-600">₱{fmt(fSO.totalPayable(so))}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Header Info */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Receipt Information</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Receipt No.</label>
                                        <div className="w-full border-2 border-blue-100 bg-blue-50 rounded-xl px-3 py-2.5 text-sm font-extrabold text-blue-700 tracking-widest select-all font-mono">
                                            #{form.receiptNo}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Payment Date</label>
                                        <input type="date" value={form.paymentDate}
                                            onChange={e => setForm(p => ({ ...p, paymentDate: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Account No.</label>
                                        <input type="text" value={form.accountNo} placeholder="Account no."
                                            onChange={e => setForm(p => ({ ...p, accountNo: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono" />
                                    </div>

                                    {/* Payment Mode */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Payment Mode</label>
                                        <div className="flex gap-2">
                                            {["CASH", "CHECK"].map(mode => (
                                                <button key={mode}
                                                    onClick={() => setForm(p => ({ ...p, paymentMode: mode }))}
                                                    className={`flex-1 py-2.5 rounded-xl text-xs font-bold border-2 cursor-pointer transition-all ${form.paymentMode === mode ? "border-blue-500 bg-blue-600 text-white" : "border-gray-200 bg-white text-gray-500 hover:border-blue-200"}`}>
                                                    {mode}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Registered Name <span className="text-red-500">*</span></label>
                                        <input type="text" value={form.receivedFrom} placeholder="Customer / Registered Name"
                                            onChange={e => setForm(p => ({ ...p, receivedFrom: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-semibold" />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">TIN</label>
                                        <input type="text" value={form.tin} placeholder="TIN"
                                            onChange={e => setForm(p => ({ ...p, tin: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Business Address</label>
                                        <input type="text" value={form.address} placeholder="Business address"
                                            onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                </div>
                            </div>

                            {/* Details / Nature of Service */}
                            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Description / Nature of Service</h3>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1">
                                            Total: ₱{fmt(totalAmount)}
                                        </span>
                                        <button onClick={addDetail}
                                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold text-blue-700 cursor-pointer transition-all hover:bg-blue-100"
                                            style={{ background: "#dbeafe", border: "1.5px solid #93c5fd" }}>
                                            <Plus size={12} /> Add Row
                                        </button>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-blue-800 text-white">
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-center w-8">#</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-left">Description of Transaction / Nature of Service</th>
                                                <th className="px-2 py-2 text-[10px] font-bold uppercase text-right w-32">Amount</th>
                                                <th className="px-2 py-2 w-10"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.details.length === 0 ? (
                                                <tr><td colSpan={4} className="py-8 text-center text-slate-400 text-xs">No items yet. Click "Add Row".</td></tr>
                                            ) : form.details.map((item, idx) => (
                                                <DetailRow
                                                    key={idx}
                                                    index={idx}
                                                    item={item}
                                                    onChange={(field, value) => updateDetail(idx, field, value)}
                                                    onRemove={() => removeDetail(idx)}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Check Payment Details */}
                            {form.paymentMode === "CHECK" && (
                                <div className="bg-white rounded-2xl border border-amber-100 p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-4">Check Payment Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Check No.</label>
                                            <input type="text" value={form.checkNo} placeholder="Check number"
                                                onChange={e => setForm(p => ({ ...p, checkNo: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white font-mono" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Bank</label>
                                            <input type="text" value={form.bank} placeholder="Bank name"
                                                onChange={e => setForm(p => ({ ...p, bank: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">Check Date</label>
                                            <input type="date" value={form.checkDate}
                                                onChange={e => setForm(p => ({ ...p, checkDate: e.target.value }))}
                                                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                            style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={() => setShowForm(false)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Cancel
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="text-right mr-2">
                                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">Total Payable</p>
                                    <p className="text-lg font-extrabold text-blue-700">₱{fmt(totalAmount)}</p>
                                </div>
                                <button onClick={handleSubmit} disabled={formLoading}
                                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer disabled:opacity-50 transition-all hover:-translate-y-0.5"
                                    style={{ background: "linear-gradient(135deg,#1e40af,#2563eb)", boxShadow: "0 2px 10px rgba(37,99,235,0.35)", border: "none" }}>
                                    <Save size={15} />
                                    {formLoading ? "Saving..." : "Save Receipt"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ VIEW MODAL ══ */}
            {selectedRow && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setSelectedRow(null); }}>
                    <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden"
                        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        <div style={gradBlue} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><Receipt size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Collection</p>
                                    <h2 className="text-white font-extrabold text-base leading-none">Collection Receipt</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Receipt No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-wider font-mono">#{fR.receiptNo(selectedRow)}</p>
                                </div>
                                <button onClick={() => setSelectedRow(null)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Meta bar */}
                        <div className="px-7 py-2.5 flex items-center gap-1 flex-wrap flex-shrink-0" style={{ background: "rgba(30,58,138,0.93)" }}>
                            {[
                                { icon: CalendarDays, label: "Date", value: fmtDate(fR.paymentDate(selectedRow)) },
                                { icon: Users, label: "Customer", value: fR.receivedFrom(selectedRow) || "—" },
                                { icon: Building2, label: "SO Ref.", value: fR.soNo(selectedRow) ? `${fR.soPrefix(selectedRow)}${fR.soNo(selectedRow)}` : "—" },
                            ].map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="w-px h-4 bg-white/20 mx-3" />}
                                        <span className="flex items-center gap-2 text-xs">
                                            <Icon size={12} className="text-blue-400" />
                                            <span className="text-white/50">{item.label}:</span>
                                            <span className="text-white font-semibold">{item.value}</span>
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                            <div className="p-6">
                                <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                                    <ReceiptDocument data={selectedRow} />
                                </div>
                            </div>
                        </div>

                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0"
                            style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={async () => {
                                setPdfLoading(true);
                                try { await exportReceiptPDF(selectedRow); }
                                catch (err) { Swal.fire({ icon: "error", title: "PDF Export Failed", text: err.message, confirmButtonColor: "#dc2626" }); }
                                finally { setPdfLoading(false); }
                            }} disabled={pdfLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all cursor-pointer hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)", boxShadow: "0 2px 10px rgba(220,38,38,0.35)", border: "none" }}>
                                <Download size={15} />
                                {pdfLoading ? "Generating PDF..." : "Export PDF"}
                            </button>
                            <button onClick={() => setSelectedRow(null)}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollectionReceipt;